import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  UserCircle, PencilSimple, Plus, Trash, Eye, Link as LinkIcon,
  TwitterLogo, YoutubeLogo, InstagramLogo, Globe, ChartLineUp, Users, Stack
} from '@phosphor-icons/react';

const CATEGORIES = [
  "Technology", "Finance", "Gaming", "Health", "Education",
  "Travel", "E-Commerce", "Lifestyle", "Entertainment", "Sports",
  "Food", "Fashion", "Crypto", "SaaS", "Marketing"
];

export default function CreatorStudio() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [profileExists, setProfileExists] = useState(false);
  const [collections, setCollections] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    display_name: '', bio: '', custom_url: '', categories: [],
    audience_tags: '', email_capture_enabled: false, disclosure_text: '',
    social_links: { twitter: '', youtube: '', instagram: '', tiktok: '', website: '' }
  });
  const [newCollection, setNewCollection] = useState({ name: '', description: '', type: 'custom' });
  const [showNewCol, setShowNewCol] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [profRes, colRes, anaRes] = await Promise.all([
        api.get('/creators/profile'),
        api.get('/creators/collections').catch(() => ({ data: [] })),
        api.get('/creators/analytics').catch(() => ({ data: null })),
      ]);
      const p = profRes.data;
      if (p.exists) {
        setProfile(p);
        setProfileExists(true);
        setForm({
          display_name: p.display_name || '', bio: p.bio || '',
          custom_url: p.custom_url || '', categories: p.categories || [],
          audience_tags: (p.audience_tags || []).join(', '),
          email_capture_enabled: p.email_capture_enabled || false,
          disclosure_text: p.disclosure_text || '',
          social_links: p.social_links || { twitter: '', youtube: '', instagram: '', tiktok: '', website: '' },
        });
      }
      setCollections(colRes.data || []);
      setAnalytics(anaRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveProfile = async () => {
    try {
      const payload = {
        ...form,
        audience_tags: form.audience_tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      await api.post('/creators/profile', payload);
      toast.success('Profile saved');
      setEditing(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const createCollection = async () => {
    if (!newCollection.name) return;
    try {
      await api.post('/creators/collections', newCollection);
      toast.success('Collection created');
      setShowNewCol(false);
      setNewCollection({ name: '', description: '', type: 'custom' });
      fetchData();
    } catch (e) {
      toast.error('Failed to create collection');
    }
  };

  const deleteCollection = async (id) => {
    try {
      await api.delete(`/creators/collections/${id}`);
      toast.success('Collection deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const toggleCategory = (cat) => {
    setForm(prev => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : prev.categories.length < 5 ? [...prev.categories, cat] : prev.categories
    }));
  };

  if (loading) return <div className="p-8 text-center text-zinc-500">Loading Creator Studio...</div>;

  return (
    <div className="space-y-6" data-testid="creator-studio-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Creator Studio</h1>
          <p className="text-sm text-zinc-500 mt-1">Build your storefront, manage collections, track performance</p>
        </div>
        {profileExists && profile?.custom_url && (
          <a
            href={`${window.location.origin}/storefront/${profile.custom_url}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
            data-testid="view-storefront-link"
          >
            <Eye size={16} /> View Public Storefront
          </a>
        )}
      </div>

      {/* Analytics Summary */}
      {analytics?.kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'Clicks', value: analytics.kpis.total_clicks?.toLocaleString(), icon: ChartLineUp },
            { label: 'Conversions', value: analytics.kpis.total_conversions?.toLocaleString() },
            { label: 'Revenue', value: `$${analytics.kpis.total_revenue?.toLocaleString()}` },
            { label: 'Conv Rate', value: `${analytics.kpis.conversion_rate}%` },
            { label: 'Followers', value: analytics.kpis.followers?.toLocaleString(), icon: Users },
            { label: 'Views', value: analytics.kpis.storefront_views?.toLocaleString(), icon: Eye },
          ].map((stat, i) => (
            <Card key={i} className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-3">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{stat.label}</p>
                <p className="text-lg font-bold mt-0.5">{stat.value || '0'}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Profile Editor */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <UserCircle size={20} className="text-blue-400" />
            Creator Profile
          </CardTitle>
          {profileExists && !editing && (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="edit-profile-btn" className="text-blue-400 hover:text-blue-300">
              <PencilSimple size={16} className="mr-1" /> Edit
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {(!profileExists || editing) ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Display Name</label>
                  <Input data-testid="profile-display-name" value={form.display_name} onChange={(e) => setForm(p => ({...p, display_name: e.target.value}))} className="bg-zinc-800 border-zinc-700" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Custom URL</label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-500">/storefront/</span>
                    <Input data-testid="profile-custom-url" value={form.custom_url} onChange={(e) => setForm(p => ({...p, custom_url: e.target.value}))} className="bg-zinc-800 border-zinc-700" placeholder="your-name" />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Bio</label>
                <textarea data-testid="profile-bio" value={form.bio} onChange={(e) => setForm(p => ({...p, bio: e.target.value}))}
                  className="w-full h-20 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500" maxLength={500} />
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Categories (max 5)</label>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORIES.map(cat => (
                    <button key={cat} onClick={() => toggleCategory(cat)} data-testid={`cat-${cat}`}
                      className={`px-2.5 py-1 rounded text-xs transition-colors ${form.categories.includes(cat) ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'}`}>
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Audience Tags (comma separated)</label>
                <Input data-testid="profile-tags" value={form.audience_tags} onChange={(e) => setForm(p => ({...p, audience_tags: e.target.value}))} className="bg-zinc-800 border-zinc-700" placeholder="developers, marketers, entrepreneurs" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-zinc-500 flex items-center gap-1 mb-1"><TwitterLogo size={12} /> Twitter</label>
                  <Input value={form.social_links.twitter} onChange={(e) => setForm(p => ({...p, social_links: {...p.social_links, twitter: e.target.value}}))} className="bg-zinc-800 border-zinc-700 text-sm" placeholder="@handle" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 flex items-center gap-1 mb-1"><YoutubeLogo size={12} /> YouTube</label>
                  <Input value={form.social_links.youtube} onChange={(e) => setForm(p => ({...p, social_links: {...p.social_links, youtube: e.target.value}}))} className="bg-zinc-800 border-zinc-700 text-sm" placeholder="Channel name" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 flex items-center gap-1 mb-1"><InstagramLogo size={12} /> Instagram</label>
                  <Input value={form.social_links.instagram} onChange={(e) => setForm(p => ({...p, social_links: {...p.social_links, instagram: e.target.value}}))} className="bg-zinc-800 border-zinc-700 text-sm" placeholder="@handle" />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={saveProfile} data-testid="save-profile-btn" className="bg-blue-600 hover:bg-blue-700">
                  {profileExists ? 'Save Changes' : 'Create Profile'}
                </Button>
                {editing && <Button variant="ghost" onClick={() => setEditing(false)} className="text-zinc-400">Cancel</Button>}
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-2xl font-bold text-white shrink-0">
                  {profile?.display_name?.[0] || 'C'}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    {profile?.display_name}
                    {profile?.verification_badge && <Badge className="bg-blue-600/20 text-blue-400 text-[10px]">Verified</Badge>}
                  </h3>
                  <p className="text-sm text-zinc-400 mt-1">{profile?.bio}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {profile?.categories?.map(c => (
                      <span key={c} className="px-2 py-0.5 rounded text-[10px] bg-zinc-800 text-zinc-400 border border-zinc-700">{c}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-zinc-500 text-xs">
                    {profile?.social_links?.twitter && <span className="flex items-center gap-1"><TwitterLogo size={12} /> {profile.social_links.twitter}</span>}
                    {profile?.social_links?.youtube && <span className="flex items-center gap-1"><YoutubeLogo size={12} /> {profile.social_links.youtube}</span>}
                    {profile?.social_links?.instagram && <span className="flex items-center gap-1"><InstagramLogo size={12} /> {profile.social_links.instagram}</span>}
                  </div>
                </div>
              </div>
              <div className="text-xs text-zinc-500">
                <LinkIcon size={12} className="inline mr-1" /> Public URL: /storefront/{profile?.custom_url}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Collections */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Stack size={20} className="text-emerald-400" />
            Collections ({collections.length})
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={() => setShowNewCol(true)} data-testid="new-collection-btn" className="text-emerald-400 hover:text-emerald-300">
            <Plus size={16} className="mr-1" /> New
          </Button>
        </CardHeader>
        <CardContent>
          {showNewCol && (
            <div className="mb-4 p-3 border border-zinc-700 rounded-lg bg-zinc-800/50 space-y-3">
              <Input data-testid="collection-name" value={newCollection.name} onChange={(e) => setNewCollection(p => ({...p, name: e.target.value}))} placeholder="Collection name" className="bg-zinc-800 border-zinc-700" />
              <Input value={newCollection.description} onChange={(e) => setNewCollection(p => ({...p, description: e.target.value}))} placeholder="Description" className="bg-zinc-800 border-zinc-700" />
              <select value={newCollection.type} onChange={(e) => setNewCollection(p => ({...p, type: e.target.value}))} className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300">
                <option value="custom">Custom</option>
                <option value="top_picks">Top Picks</option>
                <option value="comparison">Comparison</option>
                <option value="saved_list">Saved List</option>
              </select>
              <div className="flex gap-2">
                <Button size="sm" onClick={createCollection} data-testid="save-collection-btn" className="bg-emerald-600 hover:bg-emerald-700">Create</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowNewCol(false)} className="text-zinc-400">Cancel</Button>
              </div>
            </div>
          )}
          {collections.length === 0 ? (
            <p className="text-sm text-zinc-500">No collections yet. Create one to organize your offers.</p>
          ) : (
            <div className="space-y-2">
              {collections.map(col => (
                <div key={col.collection_id}
                  onClick={() => navigate(`/creator-studio/collections/${col.collection_id}`)}
                  className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 border border-zinc-800 hover:border-blue-500/30 hover:bg-zinc-800/50 transition-all cursor-pointer group"
                  data-testid={`collection-${col.collection_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-200 group-hover:text-blue-400 transition-colors">{col.name}</p>
                    <p className="text-xs text-zinc-500">{col.type?.replace('_', ' ')} &middot; {col.items?.length || 0} items &middot; {col.views || 0} views</p>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Badge variant="outline" className={`text-[10px] ${col.is_public ? 'text-emerald-400 border-emerald-700/40' : 'text-zinc-500 border-zinc-700'}`}>
                      {col.is_public ? 'Public' : 'Private'}
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => deleteCollection(col.collection_id)} className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/20">
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
